import { ApiError, getAuthSession, randomUUID } from '../api/client';
import { sendMessage } from '../api/chats';
import type { MessageDto, MessageSend } from '../api/types';
import { IndexedDbOutboxStore, type OutboxItem, type OutboxStore } from './outboxStore';

export const OUTBOX_MAX_AGE_MS = 7 * 24 * 60 * 60 * 1000;
export function outboxBackoff(attempts: number): number {
  return Math.min(60_000, 2000 * 2 ** Math.min(5, Math.max(0, attempts - 1)));
}
export function isPermanentSendError(error: unknown): boolean {
  return error instanceof ApiError && error.status >= 400 && error.status < 500
    && ![401, 408, 425, 429].includes(error.status);
}

type Binding = { ownerId: string; session: string; abort: AbortController };
type Sender = (chatId: string, body: MessageSend, session: string, signal: AbortSignal) => Promise<MessageDto>;
type Listener = () => void;

export class Outbox {
  private binding: Binding | null = null;
  private items: OutboxItem[] = [];
  private listeners = new Set<Listener>();
  private timer?: ReturnType<typeof setTimeout>;
  private running: Promise<void> | null = null;
  private sending = new Set<string>();
  private delivered: (message: MessageDto) => void = () => {};
  private error = false;

  constructor(private store: OutboxStore, private session = () => getAuthSession(), private send: Sender = (...args) => sendMessage(...args)) {}

  snapshot(ownerId: string): OutboxItem[] {
    return this.binding?.ownerId === ownerId && this.current(this.binding) ? this.items : [];
  }
  hasStorageError(): boolean { return this.error; }
  isSending(id: string): boolean { return this.sending.has(id); }
  subscribe = (listener: Listener): (() => void) => {
    this.listeners.add(listener);
    return () => { this.listeners.delete(listener); };
  };
  private notify() { for (const listener of this.listeners) listener(); }
  private current(binding: Binding): boolean {
    return this.binding === binding && !binding.abort.signal.aborted && this.session() === binding.session;
  }

  start(ownerId: string, delivered: (message: MessageDto) => void): void {
    this.stop();
    const session = this.session();
    if (!session || session.split(':')[1] !== ownerId) return;
    this.binding = { ownerId, session, abort: new AbortController() };
    this.delivered = delivered;
    this.kick();
  }
  stop(): void {
    this.binding?.abort.abort();
    this.binding = null;
    clearTimeout(this.timer);
    this.items = [];
    this.sending.clear();
    this.error = false;
    this.notify();
  }

  /** Резолвится только после commit IndexedDB; до этого композер сохраняет текст. */
  async enqueue(chatId: string, body: MessageSend, expectedSession: string | null): Promise<void> {
    const binding = this.binding;
    if (!binding || !this.current(binding) || expectedSession !== binding.session) throw new Error('Сессия изменилась');
    const item: OutboxItem = {
      ownerId: binding.ownerId, chatId,
      body: structuredClone({ ...body, clientMsgId: body.clientMsgId || randomUUID() }),
      createdAt: Date.now(), attempts: 0, nextAttemptAt: 0, failed: false,
    };
    let saved = item;
    // Не допускаем повторного enqueue с новым payload под уже существующим UUID.
    await this.withLock(binding, async () => {
      if (!this.current(binding)) throw new Error('Сессия изменилась');
      const existing = await this.store.list(binding.ownerId);
      if (!this.current(binding)) throw new Error('Сессия изменилась');
      const previous = existing.find(entry => entry.body.clientMsgId === item.body.clientMsgId);
      saved = previous ?? { ...item, createdAt: Math.max(item.createdAt, ...existing.map(entry => entry.createdAt + 1)) };
      if (!previous) await this.store.put(saved);
    }, 'write');
    if (this.current(binding)) {
      this.items = [...this.items.filter(entry => entry.body.clientMsgId !== saved.body.clientMsgId), saved];
      this.notify();
    }
    this.kick();
  }

  async retry(ownerId: string, id: string): Promise<void> {
    const binding = this.binding;
    if (!binding || binding.ownerId !== ownerId) return;
    await this.withLock(binding, async () => {
      const item = (await this.store.list(ownerId)).find(entry => entry.body.clientMsgId === id);
      if (item && this.current(binding)) {
        await this.store.put({ ...item, failed: false, attempts: 0, nextAttemptAt: 0, createdAt: Date.now() });
      }
    });
    if (this.current(binding)) await this.refresh(binding);
    this.kick();
  }
  async cancel(ownerId: string, id: string): Promise<void> {
    const binding = this.binding;
    if (!binding || binding.ownerId !== ownerId) return;
    // Уже начатый HTTP-запрос мог быть принят сервером; отменяем только ожидающее сообщение.
    await this.withLock(binding, async () => {
      if (this.current(binding)) await this.store.remove(ownerId, id);
    });
    if (this.current(binding)) await this.refresh(binding);
    this.kick();
  }

  private async withLock<T>(binding: Binding, action: () => Promise<T>, kind = 'send'): Promise<T> {
    if (typeof navigator !== 'undefined' && navigator.locks) {
      return navigator.locks.request('hc-outbox:' + kind + ':' + binding.ownerId, { signal: binding.abort.signal }, action);
    }
    return action();
  }
  private async refresh(binding: Binding): Promise<void> {
    const items = await this.store.list(binding.ownerId);
    if (this.current(binding)) { this.items = items; this.error = false; this.notify(); }
  }

  kick = (): void => { void this.flush(); };
  /** Один проход; таймер остаётся и при пустой очереди, чтобы подхватить изменения другой вкладки. */
  flush(): Promise<void> {
    clearTimeout(this.timer);
    const binding = this.binding;
    if (!binding) return Promise.resolve();
    if (!this.current(binding)) { this.stop(); return Promise.resolve(); }
    if (this.running) return this.running;
    this.running = this.withLock(binding, async () => {
      const blocked = new Set<string>();
      const entries = await this.store.list(binding.ownerId);
      if (this.current(binding)) { this.items = entries; this.notify(); }
      for (const item of entries) {
        if (!this.current(binding)) break;
        if (item.failed || blocked.has(item.chatId)) continue;
        if (Date.now() - item.createdAt > OUTBOX_MAX_AGE_MS) {
          await this.store.put({ ...item, failed: true });
          continue;
        }
        if (item.nextAttemptAt > Date.now()) { blocked.add(item.chatId); continue; }
        this.sending.add(item.body.clientMsgId);
        this.notify();
        let message: MessageDto;
        const attempt = new AbortController();
        const abort = () => attempt.abort();
        binding.abort.signal.addEventListener('abort', abort, { once: true });
        const timeout = setTimeout(abort, 30_000);
        try {
          message = await this.send(item.chatId, item.body, binding.session, attempt.signal);
        } catch (error) {
          if (!this.current(binding)) break;
          const attempts = item.attempts + 1;
          const failed = isPermanentSendError(error);
          await this.store.put({ ...item, attempts, failed, nextAttemptAt: Date.now() + outboxBackoff(attempts) });
          if (!failed) blocked.add(item.chatId);
          continue;
        } finally {
          clearTimeout(timeout);
          binding.abort.signal.removeEventListener('abort', abort);
          this.sending.delete(item.body.clientMsgId);
          this.notify();
        }
        if (!this.current(binding)) break;
        // Если delete не зафиксируется, повтор вернёт тот же server message по UUID.
        await this.store.remove(binding.ownerId, item.body.clientMsgId);
        if (this.current(binding)) this.delivered(message);
      }
      if (this.current(binding)) await this.refresh(binding);
    }).catch(() => {
      if (this.current(binding)) { this.error = true; this.notify(); }
    }).finally(() => {
      this.running = null;
      if (this.binding && this.current(this.binding)) this.timer = setTimeout(this.kick, 2000);
    });
    return this.running;
  }
}

export const outbox = new Outbox(new IndexedDbOutboxStore());
